import { InternalOnlyPublishVisibility } from "../../../../contexts/PublishingContext.js";
import { LayoutBuilderRenderer } from "../../../../components/LayoutBuilder/LayoutBuilderRenderer.js";
import { FormBuilderFieldsContextProvider } from "../../contexts/FormBuilderFieldsContext.js";
import { FieldFormRenderer } from "./components/FieldFormRenderer.js";
import { getSignatureFieldError } from "../Fields/Signature/validate.js";
import { INTERNAL_CONTENT_CONTAINER_CLASSES } from "../Fields/constants.js";
import { useFormSchema } from "../../hooks/useFormSchema.js";
import { useCallback, useRef, useState } from "react";
import { FormLayout, Loading } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import { useDebounceEffect } from "ahooks";
import warnOnce from "warn-once";
//#region src/modules/FormBuilder/components/FormBuilderFormRenderer/index.tsx
var layoutBuilderRendererStyles = { Component: { padding: "0.25em" } };
function FormBuilderFormRenderer({ dateTimeEndFieldId, dateTimeStartFieldId, formId, formValues, initialValue, onFormErrorStateChange, onFormInputChange, onFormValueChange }) {
	const { form, loading } = useFormSchema(formId);
	const [errorsByFieldId, setErrorsByFieldId] = useState();
	const formErrors = () => {
		if (form?.formBuilder) {
			const reduced = Object.keys(form?.formBuilder?.fieldsById).reduce((acc, key) => {
				const field = form.formBuilder.fieldsById[key];
				if (field.type === "signature") {
					const message = getSignatureFieldError(field.options, formValues?.[key]);
					if (message) acc[key] = message;
					return acc;
				}
				if (field.options.required && !formValues?.[key]) acc[key] = "This field is required";
				else if (field.options.required && (field.type === "activitytypes" || field.type === "datetime" || field.type === "event" || field.type === "files" || field.type === "location" || field.type === "record" || field.type === "report" || field.type === "select" || field.type === "user") && !formValues?.[key].length) acc[key] = "This field is required";
				if (field.type === "datetime" && dateTimeStartFieldId && dateTimeEndFieldId) {
					if (new Date(formValues?.[dateTimeStartFieldId]) >= new Date(formValues?.[dateTimeEndFieldId])) acc[key] = "End date/time must be after start date/time";
				}
				return acc;
			}, {});
			if (Object.keys(reduced).length) {
				setErrorsByFieldId(reduced);
				onFormErrorStateChange?.(reduced);
			} else {
				setErrorsByFieldId(void 0);
				onFormErrorStateChange?.();
			}
		}
	};
	useDebounceEffect(() => {
		formErrors();
	}, [formValues], { wait: 300 });
	const local = useRef({
		onFormInputChange,
		onFormValueChange
	});
	local.current.onFormInputChange = onFormInputChange;
	local.current.onFormValueChange = onFormValueChange;
	const handleFormValueChange = useCallback((fields) => {
		if (local.current.onFormInputChange) local.current.onFormInputChange({
			fields,
			formId
		});
		if (local.current.onFormValueChange) local.current.onFormValueChange({
			fields,
			formId
		});
	}, [formId]);
	const renderComponentFromId = useCallback((componentId) => {
		if (!form) return null;
		const field = form.formBuilder.fieldsById[componentId];
		if (!field) {
			warnOnce(true, `FormBuilderFormRenderer componentId ("${componentId}") not found in fieldsById`);
			return null;
		}
		if (field.internal) return /* @__PURE__ */ jsx(InternalOnlyPublishVisibility, {
			isInternal: field.internal,
			children: /* @__PURE__ */ jsx("div", {
				className: INTERNAL_CONTENT_CONTAINER_CLASSES,
				children: /* @__PURE__ */ jsx(FieldFormRenderer, { field })
			})
		});
		return /* @__PURE__ */ jsx(FieldFormRenderer, { field });
	}, [form]);
	if (loading || !form) return /* @__PURE__ */ jsx("div", {
		className: "my-base flex w-full justify-between align-middle",
		children: /* @__PURE__ */ jsx(Loading, { message: "Loading Form" })
	});
	if (form.formBuilder.formLayout.schemaVersion !== "1") return /* @__PURE__ */ jsxs("div", {
		className: "text-color-critical",
		children: [
			"Not a valid Form Builder schema (Schema Version: \"",
			form.formBuilder.formLayout.schemaVersion,
			"\")"
		]
	});
	return /* @__PURE__ */ jsx(FormBuilderFieldsContextProvider, {
		errorsByFieldId,
		initialValues: initialValue,
		onFormValueChange: handleFormValueChange,
		children: /* @__PURE__ */ jsx(FormLayout, {
			horizontal: false,
			children: /* @__PURE__ */ jsx(LayoutBuilderRenderer, {
				layout: form.formBuilder.formLayout,
				renderComponentFromId,
				styles: layoutBuilderRendererStyles
			})
		})
	});
}
//#endregion
export { FormBuilderFormRenderer };

//# sourceMappingURL=index.js.map